/**
 * src/hooks/useMetaSheetsData.ts
 *
 * Fetches live Meta Ads data from the Google Apps Script Web App,
 * adapts it into MetaLeadGenRow arrays, and manages loading/error state.
 *
 * Returns four typed row arrays — one per breakdown table — plus debug info.
 * Falls back to empty arrays on error so the page can still render with mock data.
 */

import { useState, useEffect, useCallback } from "react";
import { fetchAllMetaData } from "../services/metaSheetsApi.js";
import {
  adaptSheetsResponse,
  type MetaSheetsAdaptedData,
} from "../pages/ads/metaLeadGen/adapters/googleSheetsAdapter";
import type { MetaLeadGenRow } from "../pages/ads/metaLeadGen/types";

export interface UseMetaSheetsDataReturn {
  kpiRows: MetaLeadGenRow[];
  ageGenderRows: MetaLeadGenRow[];
  deviceRows: MetaLeadGenRow[];
  platformRows: MetaLeadGenRow[];
  loading: boolean;
  error: string | null;
  debugInfo: MetaSheetsAdaptedData["debugInfo"] | null;
  refetch: () => void;
}

export function useMetaSheetsData(): UseMetaSheetsDataReturn {
  const [adapted, setAdapted] = useState<MetaSheetsAdaptedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Verify server-side connection config first
      const dbg = await fetch("/api/meta-ads/debug").then(r => r.json()).catch(() => null) as {
        apiUrlConfigured?: boolean;
        apiKeyConfigured?: boolean;
        endpointFixed?: string;
        finalUrlPreview?: string;
        keyIncluded?: boolean;
      } | null;
      console.group("[useMetaSheetsData] Connection debug");
      console.log("API URL configured:    ", dbg?.apiUrlConfigured ?? "unknown");
      console.log("API KEY configured:    ", dbg?.apiKeyConfigured ?? "unknown");
      console.log("endpoint fixed to:     ", dbg?.endpointFixed ?? "unknown");
      console.log("key= included in URL:  ", dbg?.keyIncluded ?? "unknown");
      console.log("Final URL preview:     ", dbg?.finalUrlPreview ?? "unknown");
      console.groupEnd();

      const raw = await fetchAllMetaData();
      const result = adaptSheetsResponse(raw as Parameters<typeof adaptSheetsResponse>[0]);
      setAdapted(result);

      console.group("[useMetaSheetsData] Live data loaded ✓");
      console.log("raw_ads rows:          ", result.debugInfo.rawAdsCount);
      console.log("raw_actions rows:      ", result.debugInfo.rawActionsCount);
      console.log("selectedLeadEvent:     ", result.debugInfo.selectedLeadEvent);
      console.log("selectedPurchaseEvent: ", result.debugInfo.selectedPurchaseEvent);
      console.log("totalSpend:           $" + result.debugInfo.totalSpend.toFixed(2));
      console.log("totalLeads:            ", result.debugInfo.totalLeads);
      console.log("totalPurchases:        ", result.debugInfo.totalPurchases);
      console.groupEnd();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      console.error("[useMetaSheetsData] Fetch failed:", msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    kpiRows:       adapted?.kpiRows       ?? [],
    ageGenderRows: adapted?.ageGenderRows ?? [],
    deviceRows:    adapted?.deviceRows    ?? [],
    platformRows:  adapted?.platformRows  ?? [],
    loading,
    error,
    debugInfo:     adapted?.debugInfo     ?? null,
    refetch:       load,
  };
}
